// ** Third Party Imports
import * as RadixDialog from '@radix-ui/react-dialog'

// ** Icon Imports
import { XIcon } from '../../icons'

// ** Styled Components
import {
	StyledOverlay,
	StyledContent,
	ContentPosition,
	CloseButton,
	StyledHeading
} from './styles'

// ** Type Imports
import { type CSSProperties } from 'react'

interface DialogProps {
	children?: React.ReactNode
	open?: boolean
	onOpenChange?: (open: boolean) => void
}

/**
 * Dialog component
 * @example
 * <Dialog>
 *    <Dialog.Trigger>
 *      <Button>Open Dialog</Button>
 *    </Dialog.Trigger>
 *    <Dialog.Content>
 *      <Dialog.Close />
 *      <Dialog.Title>Title</Dialog.Title>
 *       ...
 *    </Dialog.Content>
 * </Dialog>
 */
export const Dialog = ({ children, ...props }: DialogProps) => {
	return <RadixDialog.Root {...props}>{children}</RadixDialog.Root>
}

Dialog.Trigger = ({ children }: { children?: React.ReactNode }) => {
	return <RadixDialog.Trigger asChild>{children}</RadixDialog.Trigger>
}

Dialog.Content = ({
	children,
	className,
	overlayStyles,
	contentPositionStyles,
	preventOutsideClick = false
}: {
	children?: React.ReactNode
	className?: string
	overlayStyles?: CSSProperties
	contentPositionStyles?: CSSProperties
	preventOutsideClick?: boolean
}) => {
	return (
		<RadixDialog.Portal id='radix-dialog-portal'>
			<StyledOverlay
				$styles={overlayStyles}
				onPointerDown={(e) => {
					if (preventOutsideClick) {
						e.preventDefault()
					}
				}}
			/>
			<ContentPosition $styles={contentPositionStyles}>
				<StyledContent
					className={className}
					onPointerDownOutside={(e) => {
						if (preventOutsideClick) {
							e.preventDefault()
						}
					}}
					onInteractOutside={(e) => {
						if (preventOutsideClick) {
							e.preventDefault()
						}
					}}
				>
					{children}
				</StyledContent>
			</ContentPosition>
		</RadixDialog.Portal>
	)
}

Dialog.Close = ({
	children,
	styles
}: {
	children?: React.ReactNode
	styles?: CSSProperties
}) => {
	return (
		<RadixDialog.Close asChild>
			{children ?? (
				<CloseButton
					icon={<XIcon />}
					ariaLabel='Close'
					width={1.5}
					height={1.5}
					$styles={styles}
				/>
			)}
		</RadixDialog.Close>
	)
}

Dialog.Title = ({
	children,
	...props
}: {
	children?: React.ReactNode
	className?: string
	style?: CSSProperties
}) => {
	return (
		<RadixDialog.Title asChild {...props}>
			<StyledHeading variant='h2' weight='semi-bold'>
				{children}
			</StyledHeading>
		</RadixDialog.Title>
	)
}
